import { AnimatePresence, motion } from 'framer-motion';
import { useMemo, useRef, useState } from 'react';
import ReactHotkeys from 'react-hot-keys';
import styled, { css } from 'styled-components';

import { MenuArrowDownSvg } from '../../assets/icons';
import theme from '../../assets/theme';
import { useOnOutsideClick } from '../../hooks/useOnOutsideClick';
import { IconWrapper } from '../_atoms/IconWrapper';

export type SortDropdownOption = {
  value: string;
  displayName: string | React.ReactNode;
  icon: React.ReactNode;
};

export const SortDropdown = ({
  onSelect,
  options,
  value,
}: {
  value: string | undefined;
  options: SortDropdownOption[];
  onSelect: (value: string) => void;
}) => {
  const [showDropdown, setShowDropdown] = useState(false);

  const handleEscapePress = () => {
    setShowDropdown(false);
  };

  const handleSelect = (value: string) => {
    onSelect(value);
    setShowDropdown(false);
  };

  const selectedOption = useMemo(() => {
    return options.find(o => o.value === value);
  }, [options, value]);

  const wrapperRef = useRef<HTMLDivElement>(null);

  useOnOutsideClick(wrapperRef, handleEscapePress);

  return (
    <Wrapper ref={wrapperRef}>
      <FakeInput onClick={() => setShowDropdown(!showDropdown)}>
        {selectedOption && (
          <IconWrapper height="16px" width="16px" style={{ marginRight: 15 }}>
            {selectedOption?.icon}
          </IconWrapper>
        )}
        <span style={{ flex: 1 }}>{selectedOption?.displayName}</span>
        <IconWrapper height="12px" width="12px">
          <MenuArrowDownSvg />
        </IconWrapper>
      </FakeInput>
      <div style={{ position: 'relative', width: '100%' }}>
        <AnimatePresence>
          {showDropdown && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{
                position: 'absolute',
                top: 2,
                width: '100%',
                left: 0,
                background: 'white',
                zIndex: 9999,
                boxShadow: theme.effects.defaultShadow,
                borderRadius: 5,
                maxHeight: 250,
                overflowY: 'auto',
              }}
            >
              {options.map(option => (
                <OptionContainer
                  key={option.value}
                  $selected={option.value === value}
                  onClick={() => handleSelect(option.value)}
                >
                  <IconWrapper height="16px" width="16px" style={{ marginRight: 15 }}>
                    {option.icon}
                  </IconWrapper>
                  <span>{option.displayName}</span>
                </OptionContainer>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <ReactHotkeys keyName="Escape" onKeyUp={handleEscapePress} />
    </Wrapper>
  );
};

const OptionContainer = styled.div<{ $selected?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  padding: 10px 15px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);

  &:hover {
    background: ${theme.colors.greyLight};
  }
  user-select: none;

  ${({ $selected }) =>
    $selected &&
    css`
      background: #f6ece0;
      font-weight: bold;
    `}
`;

const FakeInput = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  line-height: 22px;
  font-size: 16px;
  outline: none;
  border-radius: 5px;
  padding: 10px 15px;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  background: white;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};

  &:hover,
  &:focus {
    /* background: ${theme.colors.greyLight}; */
  }

  &:hover {
    box-shadow: ${theme.effects.floatingCreatorElementShadowHovered};
  }
`;

const Wrapper = styled.div<{ $fluid?: boolean }>`
  cursor: pointer;
  position: relative;
  width: 240px;
`;
